// Home page — dark premium design
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fish, ChevronRight, Wifi, WifiOff, Wind, Thermometer, Gauge, Navigation, Download, Sun, Moon, Droplets } from 'lucide-react';
import { getAllTrips, getAllCatches } from '@/lib/local-db';
import type { Trip, WeatherSnapshot } from '@/lib/world-model';
import { fetchWeather } from '@/lib/weather-adapter';
import { getCurrentPosition } from '@/lib/gps';
import { v4 as uuidv4 } from 'uuid';
import { saveTrip } from '@/lib/local-db';
import { getSolarContext, getMoonContext, getSolunarPeriods } from '@/lib/field-conditions';
import type { SolunarPeriod } from '@/lib/field-conditions';
import { getStationReadings } from '@/lib/water-data';
import type { RiverReading } from '@/lib/water-data';

export function HomePage() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [totalCatches, setTotalCatches] = useState(0);
  const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [starting, setStarting] = useState(false);
  const [riverReadings, setRiverReadings] = useState<RiverReading[]>([]);

  // Solunar/solar — computed offline from suncalc
  const solar = getSolarContext();
  const moon = getMoonContext();
  const solunar = getSolunarPeriods();
  const activePeriod = solunar.find(p => p.isActive);
  const nextPeriod = solunar.find(p => p.start > new Date());

  useEffect(() => {
    getAllTrips().then(setTrips);
    getAllCatches().then(c => setTotalCatches(c.length));
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    if (!navigator.onLine) return;
    getCurrentPosition()
      .then(pos => fetchWeather(pos.lat, pos.lng))
      .then(setWeather)
      .catch(() => {});
    // Presumpscot River near West Falmouth — primary Southern Maine station
    getStationReadings('USGS-01064140').then(setRiverReadings).catch(() => {});
  }, []);

  const startTrip = async () => {
    setStarting(true);
    const now = new Date().toISOString();
    const trip: Trip = {
      id: uuidv4(),
      name: `Trip — ${new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`,
      startedAt: now,
      catchIds: [],
      waypointIds: [],
      syncStatus: 'local',
      createdAt: now,
      updatedAt: now,
    };
    await saveTrip(trip);
    navigate('/map');
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  const pressureLabel =
    weather?.pressureTrend === 'rising' ? '↑ Rising' :
    weather?.pressureTrend === 'falling' ? '↓ Falling' : '→ Steady';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', background: 'var(--bg)', color: 'var(--text-primary)', overflowY: 'auto' }}>

      {/* Hero */}
      <div style={{
        position: 'relative',
        padding: '56px 20px 28px',
        background: 'linear-gradient(160deg, #0d2b1a 0%, #0a1a2e 55%, #000 100%)',
      }}>
        {/* Connection badge */}
        <div style={{ position: 'absolute', top: 20, right: 20 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 999,
            fontSize: 11, fontWeight: 600,
            background: isOnline ? 'rgba(76,217,100,0.15)' : 'rgba(212,90,26,0.15)',
            color: isOnline ? '#4cd964' : '#d45a1a',
            border: `1px solid ${isOnline ? 'rgba(76,217,100,0.25)' : 'rgba(212,90,26,0.25)'}`,
          }}>
            {isOnline ? <Wifi size={11} /> : <WifiOff size={11} />}
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>

        <p style={{ color: '#4a7a5e', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{greeting}, fisherman</p>
        <h1 style={{ fontSize: 34, fontWeight: 700, letterSpacing: -1, lineHeight: 1, marginBottom: 4 }}>BluGuide</h1>
        <p style={{ color: '#3a6a4e', fontSize: 13 }}>Southern Maine · Fly Fishing</p>

        {/* Conditions */}
        <div style={{ display: 'flex', gap: 20, marginTop: 20 }}>
          <Cond icon={<Thermometer size={13} color="#4a7a5e" />} value={weather ? `${Math.round(weather.tempF)}°F` : '—'} />
          <Cond icon={<Wind size={13} color="#4a7a5e" />} value={weather ? `${Math.round(weather.windMph)} mph` : '—'} />
          <Cond icon={<Gauge size={13} color="#4a7a5e" />} value={weather ? pressureLabel : '—'} />
        </div>
      </div>

      {/* Start Trip CTA */}
      <div style={{ padding: '16px 16px 0' }}>
        <button
          onClick={startTrip}
          disabled={starting}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, padding: '16px 0', borderRadius: 18, border: 'none',
            background: 'linear-gradient(135deg, #1a5c38, #1a4a2e)',
            color: '#fff', fontSize: 16, fontWeight: 600,
            boxShadow: '0 4px 24px rgba(26,74,46,0.6)',
            cursor: 'pointer', opacity: starting ? 0.6 : 1,
            transition: 'transform 0.15s, opacity 0.15s',
            fontFamily: 'inherit',
          }}
          onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.98)')}
          onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
        >
          <Navigation size={18} />
          {starting ? 'Starting…' : 'Start Trip'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '16px 16px 0' }}>
        <StatCard value={totalCatches} label="Total Catches" color="#ffd700" />
        <StatCard value={trips.length} label="Trips Logged" color="#007aff" />
      </div>

      {/* Recent Trips */}
      <div style={{ padding: '20px 16px 0' }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Recent Trips
        </p>
        {trips.length === 0 ? (
          <div style={{
            background: 'var(--surface-2)', borderRadius: 18,
            border: '1px solid var(--border)',
            padding: '28px 20px', textAlign: 'center',
          }}>
            <Fish size={28} color="var(--surface-4)" style={{ margin: '0 auto 8px' }} />
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No trips yet.</p>
            <p style={{ color: 'var(--surface-4)', fontSize: 12, marginTop: 4 }}>Tap Start Trip to begin.</p>
          </div>
        ) : (
          <div style={{ background: 'var(--surface-2)', borderRadius: 18, border: '1px solid var(--border)', overflow: 'hidden' }}>
            {trips.slice(0, 5).map((trip, i) => (
              <div key={trip.id}>
                <TripRow trip={trip} onPress={() => navigate('/map')} />
                {i < Math.min(trips.length, 5) - 1 && (
                  <div style={{ height: 1, background: 'var(--border)', marginLeft: 56 }} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* River Conditions — USGS live data */}
      <div style={{ padding: '12px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Presumpscot River
          </p>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>USGS · Live</span>
        </div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 18, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {riverReadings.length === 0 ? (
            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Droplets size={14} color="#4a7a5e" />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {isOnline ? 'Loading…' : 'Offline — no live data'}
              </span>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 0 }}>
              {riverReadings.map((r, i) => (
                <div key={r.paramLabel} style={{
                  padding: '12px 14px',
                  borderRight: i < riverReadings.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
                    {r.paramLabel.replace(/ \(.*\)/, '')}
                  </p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
                    {parseFloat(r.value).toFixed(r.paramLabel.includes('Temp') ? 1 : 0)}
                  </p>
                  <p style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                    {r.paramLabel.match(/\(([^)]+)\)/)?.[1] ?? ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Field Conditions — offline, always works */}
      <div style={{ padding: '12px 16px 0' }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Field Conditions
        </p>
        <div style={{ background: 'var(--surface-2)', borderRadius: 18, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {/* Solunar */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Moon size={14} color="#4a7a5e" />
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Solunar</span>
              </div>
              {activePeriod ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#4cd964', background: 'rgba(76,217,100,0.12)', padding: '2px 8px', borderRadius: 999 }}>
                  {activePeriod.label} Active
                </span>
              ) : nextPeriod ? (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Next: {nextPeriod.label} {nextPeriod.str}</span>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' as const }}>
              {solunar.map((p, i) => (
                <SolunarChip key={i} period={p} />
              ))}
            </div>
          </div>
          {/* Solar */}
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Sun size={14} color="#4a7a5e" />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Solar</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <SolarStat label="Sunrise" value={solar.sunriseStr} highlight={solar.inMorningGoldenHour} />
              <SolarStat label="Sunset" value={solar.sunsetStr} highlight={solar.inEveningGoldenHour} />
              <SolarStat label="Golden AM" value={solar.goldenMorningStr} highlight={solar.inMorningGoldenHour} />
              <SolarStat label="Golden PM" value={solar.goldenEveningStr} highlight={solar.inEveningGoldenHour} />
            </div>
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{moon.phaseName}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{Math.round(moon.fraction * 100)}% illuminated</span>
            </div>
          </div>
        </div>
      </div>

      {/* Offline Maps */}
      <div style={{ padding: '12px 16px 32px' }}>
        <button
          onClick={() => navigate('/downloads')}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            background: 'var(--surface-2)', borderRadius: 18, border: '1px solid var(--border)',
            padding: '14px 16px', cursor: 'pointer', color: 'inherit', fontFamily: 'inherit',
            transition: 'background 0.15s',
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 12, flexShrink: 0,
            background: 'rgba(26,74,46,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Download size={16} color="#4a7a5e" />
          </div>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>Offline Maps</p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Download Maine for offline use</p>
          </div>
          <ChevronRight size={16} color="var(--text-muted)" />
        </button>
      </div>
    </div>
  );
}

function Cond({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon}
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

function StatCard({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div style={{
      background: 'var(--surface-2)', borderRadius: 18,
      border: '1px solid var(--border)', padding: '16px',
    }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        {label}
      </p>
      <p style={{ fontSize: 36, fontWeight: 700, lineHeight: 1, color }}>{value}</p>
    </div>
  );
}

function TripRow({ trip, onPress }: { trip: Trip; onPress: () => void }) {
  const isActive = !trip.endedAt;
  return (
    <button
      onClick={onPress}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 16px', background: 'transparent', border: 'none',
        cursor: 'pointer', color: 'inherit', fontFamily: 'inherit',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 12, flexShrink: 0,
        background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: isActive ? '#4cd964' : 'var(--surface-4)',
        }} />
      </div>
      <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {trip.name}
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {new Date(trip.startedAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          {' · '}{trip.catchIds.length} {trip.catchIds.length === 1 ? 'catch' : 'catches'}
        </p>
      </div>
      <ChevronRight size={16} color="var(--text-muted)" />
    </button>
  );
}

function SolunarChip({ period }: { period: SolunarPeriod }) {
  return (
    <div style={{
      padding: '5px 10px', borderRadius: 10,
      background: period.isActive ? 'rgba(76,217,100,0.12)' : 'var(--surface-3)',
      border: `1px solid ${period.isActive ? 'rgba(76,217,100,0.25)' : 'var(--border)'}`,
    }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: period.isActive ? '#4cd964' : 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {period.label}
      </p>
      <p style={{ fontSize: 11, color: period.isActive ? 'var(--text-primary)' : 'var(--text-secondary)', marginTop: 1 }}>
        {period.str}
      </p>
    </div>
  );
}

function SolarStat({ label, value, highlight }: { label: string; value: string; highlight: boolean }) {
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 10,
      background: highlight ? 'rgba(255,215,0,0.08)' : 'var(--surface-3)',
      border: `1px solid ${highlight ? 'rgba(255,215,0,0.2)' : 'var(--border)'}`,
    }}>
      <p style={{ fontSize: 10, fontWeight: 600, color: highlight ? '#ffd700' : 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 2 }}>{value}</p>
    </div>
  );
}
